import { VKBridge, VKBridgeSubscribeHandler } from '@vkontakte/vk-bridge';
import { isEqual, isIOS } from '@vkontakte/vkjs';
import { EventEmitter } from '../eventEmitter';
import { logInfo } from '../logs';
import { IOS_KEYBOARD_OPENED_MIN_BOTTOM_INSET, KEYBOARD_ACTIVE_TAGS } from './consts';

const eventOptions = { passive: true, capture: false };

type SupportsDetails = { checked: true } | { checked: false; inputFocused: boolean };

type IOSKeyboardState = { supports: false } | { supports: true; height: number; opened: boolean };

/**
 * Позволяет следить за открытостью и высотой iOS клавиатуры
 * в мини-аппах, запущенных в нативных iOS клиентах.
 */
export class IOSKeyboardObserver {
  static _DEBUG_EMULATE_INVALID_BOTTOM_INSET = false;
  static _DEBUG_EMULATE_INSET_NOT_COME = false;

  private readonly emitter = new EventEmitter<{ change: VoidFunction }>();
  private readonly vkBridge: VKBridge;
  private readonly onInsetError: (type: string) => void;
  public state: IOSKeyboardState;

  constructor(vkBridge: VKBridge, onInsetError: (type: string) => void) {
    this.vkBridge = vkBridge;
    this.onInsetError = onInsetError;

    /* Состояние IOS инсет-клавиатуры */
    this.state = {
      // это примерный supports, редко бывают несоответствия,
      // при первой фоксировке значение окончательно актуализируется
      supports: this.vkBridge.isWebView() && isIOS,
      height: 0,
      opened: false,
    };
  }

  /* Проверка наличия фокуса по полю ввода */

  private static readonly checkInputFocused = () => {
    return Boolean(document.activeElement && KEYBOARD_ACTIVE_TAGS.includes(document.activeElement.tagName));
  };

  /**
   * Инициализация происходит с checked false, после первого подъёма
   * iOS клавиатуры значение supports определится однозначно
   * и checked поменяется на true
   */
  private supportsDetails: SupportsDetails = {
    checked: false,
    inputFocused: IOSKeyboardObserver.checkInputFocused(),
  };

  private updateState(state: IOSKeyboardState) {
    const prevState = this.state;
    this.state = { ...state };

    if (!isEqual(prevState, this.state)) {
      logInfo('iosKeyboardStateChanged', this.state);
      this.emitter.emit('change');
    }
  }

  private timeoutId?: NodeJS.Timeout;

  private readonly focusChangedListener = () => {
    if (!this.supportsDetails.checked) {
      this.supportsDetails.inputFocused = IOSKeyboardObserver.checkInputFocused();
      logInfo(this.supportsDetails.inputFocused ? 'input focused' : 'input blured');
    }

    if (!this.supportsDetails.checked && this.supportsDetails.inputFocused) {
      // В некоторых случаях на iOS устройствах при открытии
      // клавиатуры вообще не приходит событие об инсетах.
      // Поэтому немного ждем, и если инсет не пришел и таймер не сбросился,
      // можно пометить supports=false

      this.timeoutId && clearTimeout(this.timeoutId);
      this.timeoutId = setTimeout(() => {
        this.updateState({ supports: false });
        this.onInsetError('inset-not-come');
        this.supportsDetails = { checked: true };
        this.restart();
      }, 400);
    }
  };

  /* Слушаем инсет и обновляем стейт */

  private readonly bottomInsetRecievedHandler = (bottomInset: number) => {
    const opened = bottomInset >= IOS_KEYBOARD_OPENED_MIN_BOTTOM_INSET;
    const height = opened ? bottomInset : 0;

    if (!this.supportsDetails.checked && this.supportsDetails.inputFocused) {
      // В некоторых случаях на iOS устройствах при открытии
      // клавиатуры может прийти обычный инсет, не равный высоте клавиатуры.
      // Поэтому необходима дополнительная проверка с inputFocused, с помощью которой
      // можно пометить supports=false.

      if (opened) {
        this.updateState({ supports: true, opened, height });
      } else {
        this.updateState({ supports: false });
        this.onInsetError('invalid-bottom-inset');
      }

      this.supportsDetails = { checked: true };
      this.restart();
    } else if (this.state.supports) {
      this.updateState({ supports: true, opened, height });
    }
  };

  private readonly bridgeListener: VKBridgeSubscribeHandler = ({ detail }) => {
    if (IOSKeyboardObserver._DEBUG_EMULATE_INSET_NOT_COME) {
      return;
    }

    switch (detail.type) {
      case 'VKWebAppUpdateInsets':
      case 'VKWebAppUpdateConfig':
        if ('insets' in detail.data && detail.data.insets) {
          this.timeoutId && clearTimeout(this.timeoutId);

          if (IOSKeyboardObserver._DEBUG_EMULATE_INVALID_BOTTOM_INSET) {
            detail.data.insets.bottom = 0;
          }

          logInfo('insets', detail.data.insets);

          this.bottomInsetRecievedHandler(detail.data.insets.bottom);
        }
        break;
    }
  };

  start() {
    if (!this.state.supports) {
      return;
    }

    this.vkBridge.subscribe(this.bridgeListener);

    if (!this.supportsDetails.checked) {
      document.addEventListener('focusin', this.focusChangedListener, eventOptions);
      document.addEventListener('focusout', this.focusChangedListener, eventOptions);
    }
  }

  stop() {
    this.vkBridge.unsubscribe(this.bridgeListener);

    document.removeEventListener('focusin', this.focusChangedListener, eventOptions);
    document.removeEventListener('focusout', this.focusChangedListener, eventOptions);
  }

  private restart() {
    this.stop();
    this.start();
  }

  on = this.emitter.on;
  off = this.emitter.off;
}
